// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerFields.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, transaction, computed, action } from 'mobx';
import ReportDesignerFilter from './Izenda.ReportDesignerFilter';
import ReportDesignerDataTree from './Izenda.ReportDesignerDataTree';
import ReportBody from './Izenda.ReportBody';
import ReportFormatElement from './Izenda.ReportFormatElement';
import CONSTANTS from 'logic/data/Izenda.ReportDesignerFormatsConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import { isNil, head, sortBy } from 'lodash/fp';

const FORMAT_GROUP_CONFIG = CONSTANTS.FORMAT_GROUP_CONFIG;

@tracker
export default class ReportDesignerFields {
  @observable dataTree = new ReportDesignerDataTree();
  @observable reportFilter = null;
  @observable reportBody = null;
  @observable reportHeader = new ReportFormatElement(FORMAT_GROUP_CONFIG.HEADER);
  @observable reportFooter = new ReportFormatElement(FORMAT_GROUP_CONFIG.FOOTER);
  @observable reportTitleDescription = new ReportFormatElement(FORMAT_GROUP_CONFIG.TITLE_DESCRIPTION);
  @observable selectedFormatItem = {};
  @observable selectedFormatGroup = '';

  constructor(viewMode, isReload: boolean) {
    this.reportFilter = new ReportDesignerFilter(null, isReload);
    this.reportBody = new ReportBody(null, viewMode);
    this.initDefaultLayout();
  }

  /**
   * init default layout
   */
  @action
  initDefaultLayout(): void {
    this.reportHeader.initDefaultLayout();
    this.reportFooter.initDefaultLayout();
    this.reportTitleDescription.initDefaultLayout();
    this.cleanDirty();
  }

  /**
   * read data from report
   * @param {any} report
   * @param {boolean} keepReportBodyStates
   */
  @action
  readFromReport(report: any, keepReportBodyStates: boolean): void {
    const filter = report.reportFilter || {};
    const header = report.header || {};
    const footer = report.footer || {};
    const title = report.titleDescription || {};
    const { reportPart, snapToGrid } = report;

    // read report filters data
    this.reportFilter.populateData(filter);

    // read report body data
    this.reportBody.populateData(reportPart, snapToGrid, keepReportBodyStates);

    // read report header, footer and title
    this.reportHeader.populateData(header.items);
    this.reportFooter.populateData(footer.items);
    this.reportTitleDescription.populateData(title.items);

    this.dataTree.populateDataForCalculatedFields(report.calculatedFields);
  }

  /**
   * Build data for report saving
   */
  buildDataForSaving(reportId: any): any {
    const reportFilterData = this.reportFilter.buildDataForSaving();
    const reportBodyData = this.reportBody.buildDataForSaving();

    reportFilterData.reportId = reportId || null;

    return {
      reportFilter: reportFilterData,
      reportPart: reportBodyData.reportPart,
      snapToGrid: reportBodyData.snapToGrid
    };
  }

  /**
   * Get dirty status.
   */
  checkDirty(): boolean {
    return (
      this.isDirty ||
      (this.dataTree.checkDirty && this.dataTree.checkDirty()) ||
      (this.reportFilter.checkDirty && this.reportFilter.checkDirty()) ||
      (this.reportBody.checkDirty && this.reportBody.checkDirty()) ||
      (this.reportHeader.checkDirty && this.reportHeader.checkDirty()) ||
      (this.reportFooter.checkDirty && this.reportFooter.checkDirty())
    );
  }

  /**
   * Clean dirty status.
   */
  @action
  cleanDirty(): void {
    this.resetDirty();
    this.dataTree.cleanDirty && this.dataTree.cleanDirty();
    this.reportFilter.cleanDirty && this.reportFilter.cleanDirty();
    this.reportBody.cleanDirty && this.reportBody.cleanDirty();
    this.reportHeader.cleanDirty && this.reportHeader.cleanDirty();
    this.reportFooter.cleanDirty && this.reportFooter.cleanDirty();
  }

  /**
   * Set selected format group
   * @param {string} value
   */
  @action
  updateFormatGroup(value: any): void {
    if (isNil(value)) {
      return;
    }
    this.setSelectedFormatGroup(value);
    const firstItem = head(this.getFormatItems()) || {};
    this.setSelectedFormatItem(firstItem);

    this.markDirty();
  }

  /**
   * Get list format item
   * @return {array}
   */
  getFormatItems(): Array<any> {
    const formatGroup = this[this.selectedFormatGroup] || {};
    const listItems = formatGroup.formatItems || [];

    return sortBy(item => item.name, listItems);
  }

  /**
   * Update selected format item
   * @param {object} item
   * @param {string} group
   */
  @action
  updateFormatItem(item: any, group: any): void {
    this.setSelectedFormatGroup(group);
    this.setSelectedFormatItem(item);

    this.markDirty();
  }

  /**
   * Set selected format item
   */
  @action
  setSelectedFormatItem(item: any): void {
    this.selectedFormatItem.isSelected = false;
    item.isSelected = true;
    this.selectedFormatItem = item;
  }

  /**
   * Set selected format group
   */
  setSelectedFormatGroup(group: any): void {
    this.selectedFormatGroup = group;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerFields.js